import React, { useEffect, useState } from 'react';
import { ArrowUpIcon, SearchIcon } from 'lucide-react';
import { PeptideExplorer } from './components/PeptideExplorer/PeptideExplorer';
import { peptideData } from './components/PeptideExplorer/peptideData';
export function App() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  // Set default document title
  useEffect(() => {
    document.title = 'Peptide Vendors'; // Change this to your desired title
  }, []);
  // Track scroll position to show/hide back to top button
  useEffect(() => {
    const handleScroll = () => {
      // Show back to top button when scrolled down
      if (window.scrollY > 500) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Smooth scroll to section
  const scrollToSection = sectionId => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  // Scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  // Filter peptides based on search query
  const filteredPeptides = peptideData.filter(peptide => {
    if (!searchQuery) return false;
    return peptide.name.toLowerCase().includes(searchQuery.toLowerCase()) || peptide.description && peptide.description.toLowerCase().includes(searchQuery.toLowerCase()) || peptide.originalName && peptide.originalName.toLowerCase().includes(searchQuery.toLowerCase()) || peptide.link.toLowerCase().includes(searchQuery.toLowerCase());
  });
  // Handle search form submission
  const handleSearchSubmit = e => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSearchResults(true);
      // If we're showing search results, scroll to them
      if (filteredPeptides.length > 0) {
        setTimeout(() => {
          const resultsElement = document.getElementById('search-results');
          if (resultsElement) {
            resultsElement.scrollIntoView({
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    } else {
      setShowSearchResults(false);
    }
  };
  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setShowSearchResults(false);
  };
  return <div className="w-full min-h-full bg-white py-5 pb-10 text-black font-['Roboto',sans-serif] text-base relative" data-id="element-0">
      {/* Background accents - making them more subtle */}
      <div className="fixed top-0 left-0 w-1/3 h-full bg-gradient-to-b from-[#2290F7]/10 to-transparent pointer-events-none" data-id="element-1"></div>
      <div className="fixed top-0 right-0 w-1/3 h-full bg-gradient-to-b from-magenta-100/20 to-transparent pointer-events-none" data-id="element-2"></div>
      {/* Back to top button */}
      {showBackToTop && <button onClick={scrollToTop} className="fixed bottom-8 right-8 z-50 bg-[#2290F7] text-white p-3 rounded-full shadow-lg hover:bg-[#1a74d4] transition-all" aria-label="Back to top" data-id="element-3">
          <ArrowUpIcon size={24} data-id="element-4" />
        </button>}
      {/* Main content container */}
      <div className="max-w-[1170px] flex flex-col justify-center z-2 mx-auto pt-6" data-id="element-5">
        {/* First card - Hero Section */}
        <div id="hero" className="bg-white shadow-sm border border-gray-200 rounded-2xl py-4 my-10 w-full transform transition-all duration-300 hover:shadow-md" data-id="element-6">
          <div className="flex w-full" data-id="element-7">
            <div className="w-full px-4" data-id="element-8">
              <div className="flex flex-col justify-center items-normal w-full p-2.5 md:h-auto" data-id="element-9">
                {/* Main heading */}
                <div className="mt-10" data-id="element-10">
                  <div className="font-['Oswald',sans-serif] text-center" data-id="element-11">
                    <h1 className="text-4xl md:text-[70px] font-bold text-[#2290F7]" data-id="element-12">
                      Peptide Salvation
                    </h1>
                  </div>
                </div>
                {/* Subheading */}
                <div className="mt-12" data-id="element-13">
                  <div className="font-['Oswald',sans-serif] text-center" data-id="element-14">
                    <h1 className="text-3xl md:text-[50px] text-black" data-id="element-15">
                      Peptide Sources List
                    </h1>
                  </div>
                </div>
                {/* Global search bar */}
                <div className="mt-10 mx-auto max-w-xl w-full px-4" data-id="element-16">
                  <form onSubmit={handleSearchSubmit} className="relative" data-id="element-17">
                    <input type="text" placeholder="Search peptides..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full p-3 pl-10 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2290F7] bg-white text-lg" data-id="element-18" />
                    <SearchIcon className="absolute left-3 top-3.5 h-5 w-5 text-[#2290F7]" data-id="element-19" />
                    {searchQuery && <button type="button" onClick={clearSearch} className="absolute right-10 top-3.5 text-cyan-500 hover:text-cyan-700" aria-label="Clear search" data-id="element-20">
                        ✕
                      </button>}
                    <button type="submit" className="absolute right-3 top-3 text-cyan-600 hover:text-cyan-800 transition-colors" aria-label="Search" data-id="element-21">
                      Go
                    </button>
                  </form>
                </div>
                {/* Paragraph */}
                <div className="mx-4 md:mx-20 mt-8" data-id="element-22">
                  <div className="font-['Roboto'] text-center px-2 md:px-[100px] pt-2.5" data-id="element-23">
                    <p className="text-lg md:text-[23px] leading-[34.5px]" data-id="element-24">
                      On this page you'll find direct links to purchase ALL the
                      peptides we recommend in our seminal book,{' '}
                      <a href="https://peptides.link/book" target="_blank" rel="noopener noreferrer" className="text-[#2290F7] hover:text-[#1a74d4] underline transition-colors" data-id="element-25">
                        Peptide Salvation.
                      </a>
                    </p>
                  </div>
                </div>
                {/* Blue button */}
                <div className="text-center mt-8" data-id="element-26">
                  <button onClick={() => scrollToSection('testing')} aria-label="Go to American v2 Khavinson Peptides" className="bg-cyan-600 text-white border border-white rounded-[10px] px-6 py-[15px] text-[20px] hover:bg-cyan-700 transition-colors transform hover:scale-105 duration-200 shadow-md font-['Roboto',sans-serif]" data-id="element-27">
                    Go to American v2 Khavinson Peptides
                  </button>
                </div>
                {/* Outline button */}
                <div className="text-center mt-5" data-id="element-28">
                  <a href="/sources" aria-label="Go to Source List" className="bg-transparent border border-gray-300 rounded-[10px] px-8 py-[15px] text-[20px] text-gray-600 hover:border-gray-500 transition-colors transform hover:scale-105 duration-200 shadow-sm font-['Roboto',sans-serif] inline-block" data-id="element-29">
                    Go to Source List
                  </a>
                </div>
                {/* Bottom text */}
                <div className="mx-4 md:mx-20 my-5 mb-10" data-id="element-30">
                  <div className="font-['Roboto'] text-center px-2 md:px-[140px]" data-id="element-31">
                    <h1 className="text-xl md:text-[30px] leading-[39px] text-black" data-id="element-32">
                      After our own testing,{' '}
                      <strong data-id="element-33">
                        we do NOT recommend you purchase the Russian v1
                        Khavinson bioregulator peptides
                      </strong>
                      (except in specific cases)
                    </h1>
                    <h1 className="text-xl md:text-[30px] text-[#2290F7] mt-2 animate-bounce" data-id="element-34">
                      <br data-id="element-35" />↓
                    </h1>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Search Results */}
        {showSearchResults && searchQuery && <div id="search-results" className="max-w-[1170px] mx-auto mt-6 mb-10 bg-white shadow-sm border border-[#2290F7] rounded-2xl py-6 px-6" data-id="element-36">
            <h2 className="font-['Oswald'] text-3xl font-bold mb-4 text-center text-[#2290F7]" data-id="element-37">
              Search Results for "{searchQuery}"
            </h2>
            {filteredPeptides.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-38">
                {filteredPeptides.map((peptide, index) => <div key={peptide.id} className={`bg-white/90 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow ${index % 2 === 0 ? 'border border-[#2290F7]/30' : 'border border-[#E91E63]/30'}`} data-id="element-39">
                    <h3 className="font-['Roboto'] font-bold text-2xl mb-2" data-id="element-40">
                      {peptide.name}
                    </h3>
                    {peptide.tags && peptide.tags.includes('American Version') && <p className="font-['Roboto'] text-sm text-[#2290F7] mb-2" data-id="element-41">
                          v2 Bioregulators
                        </p>}
                    {peptide.originalName && <p className="font-['Roboto'] text-base text-gray-600 mb-2" data-id="element-42">
                        Upgraded from {peptide.originalName}
                      </p>}
                    <a href={peptide.link} target="_blank" rel="noopener noreferrer nofollow" className="text-[#2290F7] text-lg font-bold hover:text-[#1a74d4] transition-colors break-words" data-id="element-43">
                      {peptide.link.replace('https://', '')}
                    </a>
                  </div>)}
              </div> : <div className="text-center py-8" data-id="element-44">
                <p className="font-['Roboto'] text-xl text-gray-600" data-id="element-45">
                  No peptides found matching your search.
                </p>
                <button onClick={clearSearch} className="mt-4 bg-[#2290F7] text-white px-4 py-2 rounded-lg hover:bg-[#1a74d4] transition-colors font-['Roboto']" data-id="element-46">
                  Clear Search
                </button>
              </div>}
          </div>}
        {/* Second card - Testing Results */}
        <div id="testing" className="bg-white shadow-sm border border-gray-200 rounded-2xl py-4 my-10 w-full transform transition-all duration-300 hover:shadow-md scroll-mt-20" data-id="element-47">
          <div className="flex w-full" data-id="element-48">
            <div className="w-full px-4" data-id="element-49">
              <div className="flex flex-col justify-center items-normal w-full py-2.5 px-1.5" data-id="element-50">
                {/* Heading */}
                <div className="mt-10" data-id="element-51">
                  <div className="font-['Oswald',sans-serif] text-center" data-id="element-52">
                    <h1 className="text-3xl md:text-[50px] leading-tight md:leading-[65px] font-bold text-black" data-id="element-53">
                      Our Independent Testing Revealed:
                    </h1>
                  </div>
                </div>
                {/* Subheading */}
                <div className="mx-2 md:mx-5 mt-5" data-id="element-54">
                  <div className="font-['Oswald',sans-serif] text-center px-2 md:px-[140px]" data-id="element-55">
                    <h1 className="text-2xl md:text-[35px] leading-tight md:leading-[45.5px] text-black" data-id="element-56">
                      The Best Peptide Products Are <u data-id="element-57">Updated</u> &{' '}
                      <u data-id="element-58">Enhanced</u> <u data-id="element-59">American Versions</u> of Khavinson's
                      Original Formulas
                    </h1>
                  </div>
                </div>
                {/* Paragraph */}
                <div className="mx-4 md:mx-20 mt-5" data-id="element-60">
                  <div className="font-['Roboto'] text-center px-2 md:px-[100px] pt-2.5" data-id="element-61">
                    <p className="text-lg md:text-[23px] leading-[34.5px]" data-id="element-62">
                      We have an upcoming research report revealing the results
                      of our peptide tests (comparing the actual contents of
                      various different products) using mass spectrometry.
                    </p>
                  </div>
                </div>
                {/* Heading */}
                <div className="mx-4 md:mx-20 mt-8" data-id="element-63">
                  <div className="font-['Oswald',sans-serif] text-center px-2 md:px-[100px]" data-id="element-64">
                    <h1 className="text-3xl md:text-[50px] leading-tight md:leading-[65px] text-black" data-id="element-65">
                      The New, Superior American v2 Khavinson Peptide
                      Bioregulators
                    </h1>
                  </div>
                </div>
                {/* Bullet points */}
                <div className="mx-4 md:mx-20 mt-8" data-id="element-66">
                  <div className="font-['Roboto'] text-center px-2 md:px-[100px] pt-2.5" data-id="element-67">
                    <div className="bg-white/80 rounded-xl p-6 shadow-sm space-y-3 border border-[#2290F7]/30" data-id="element-68">
                      <p className="text-lg md:text-[23px] leading-[34.5px] font-['Roboto']" data-id="element-69">
                        <span className="text-[#2290F7]" data-id="element-70">●</span> The original
                        Khavinson formula but <u data-id="element-71">better in every way</u>
                      </p>
                      <p className="text-lg md:text-[23px] leading-[34.5px] font-['Roboto']" data-id="element-72">
                        <span className="text-[#2290F7]" data-id="element-73">●</span> These peptides
                        are{' '}
                        <u data-id="element-74">designed, manufactured & lab tested in the USA</u>
                      </p>
                      <p className="text-lg md:text-[23px] leading-[34.5px] font-['Roboto']" data-id="element-75">
                        <span className="text-[#2290F7]" data-id="element-76">●</span> Our own
                        analysis revealed <u data-id="element-77">significant improvements</u> over
                        the Russian version
                      </p>
                      <p className="text-lg md:text-[23px] leading-[34.5px] font-['Roboto']" data-id="element-78">
                        <span className="text-[#2290F7]" data-id="element-79">●</span> Get{' '}
                        <u data-id="element-80">50% more</u> active peptides per dose compared to the
                        Russian version
                      </p>
                      <p className="text-lg md:text-[23px] leading-[34.5px] font-['Roboto']" data-id="element-81">
                        <span className="text-[#2290F7]" data-id="element-82">●</span> Superior
                        peptide content yet <u data-id="element-83">priced lower</u> than the Russian
                        version
                      </p>
                    </div>
                  </div>
                </div>
                {/* Product list */}
                <div className="mx-4 md:mx-20 my-10 mt-8" data-id="element-84">
                  <div className="font-['Roboto'] text-center pt-2.5 text-xl md:text-[25px] leading-[37.5px]" data-id="element-85">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-86">
                      {/* Product 1 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border border-[#2290F7]/30" data-id="element-87">
                        <p className="font-['Roboto']" data-id="element-88">
                          <strong data-id="element-89">Adrenact</strong>
                        </p>
                        <p className="font-['Roboto'] text-xs text-[#2290F7]" data-id="element-90">
                          v2 Bioregulators
                        </p>
                        <p className="font-['Roboto']" data-id="element-91">Upgraded Glandokort</p>
                        <p data-id="element-92">
                          <a href="https://peptides.link/adrenact" target="_blank" rel="noopener noreferrer nofollow" className="text-[#2290F7] font-bold hover:text-[#1a74d4] transition-colors break-words font-['Roboto']" data-id="element-93">
                            peptides.link/adrenact
                          </a>
                        </p>
                      </div>
                      {/* Product 2 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border border-[#E91E63]/30" data-id="element-94">
                        <p className="font-['Roboto']" data-id="element-95">
                          <strong data-id="element-96">Bonocore</strong>
                        </p>
                        <p className="font-['Roboto'] text-xs text-[#2290F7]" data-id="element-97">
                          v2 Bioregulators
                        </p>
                        <p className="font-['Roboto']" data-id="element-98">Upgraded Bonomarlot</p>
                        <p data-id="element-99">
                          <a href="https://peptides.link/bonocore" target="_blank" rel="noopener noreferrer nofollow" className="text-[#2290F7] font-bold hover:text-[#1a74d4] transition-colors break-words font-['Roboto']" data-id="element-100">
                            peptides.link/bonocore
                          </a>
                        </p>
                      </div>
                      {/* Product 3 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border border-[#2290F7]/30" data-id="element-101">
                        <p className="font-['Roboto']" data-id="element-102">
                          <strong data-id="element-103">Cartesyl</strong>
                        </p>
                        <p className="font-['Roboto'] text-xs text-[#2290F7]" data-id="element-104">
                          v2 Bioregulators
                        </p>
                        <p className="font-['Roboto']" data-id="element-105">Upgraded Cartalax</p>
                        <p data-id="element-106">
                          <a href="https://peptides.link/cartesyl" target="_blank" rel="noopener noreferrer nofollow" className="text-[#2290F7] font-bold hover:text-[#1a74d4] transition-colors break-words font-['Roboto']" data-id="element-107">
                            peptides.link/cartesyl
                          </a>
                        </p>
                      </div>
                      {/* Product 4 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border border-[#E91E63]/30" data-id="element-108">
                        <p className="font-['Roboto']" data-id="element-109">
                          <strong data-id="element-110">Collacell</strong>
                        </p>
                        <p className="font-['Roboto'] text-xs text-[#2290F7]" data-id="element-111">
                          v2 Bioregulators
                        </p>
                        <p className="font-['Roboto']" data-id="element-112">Upgraded Sigumir</p>
                        <p data-id="element-113">
                          <a href="https://peptides.link/collacell" target="_blank" rel="noopener noreferrer nofollow" className="text-[#2290F7] font-bold hover:text-[#1a74d4] transition-colors break-words font-['Roboto']" data-id="element-114">
                            peptides.link/collacell
                          </a>
                        </p>
                      </div>
                      {/* Product 5 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border border-[#2290F7]/30" data-id="element-115">
                        <p className="font-['Roboto']" data-id="element-116">
                          <strong data-id="element-117">Endocron</strong>
                        </p>
                        <p className="font-['Roboto'] text-xs text-[#2290F7]" data-id="element-118">
                          v2 Bioregulators
                        </p>
                        <p className="font-['Roboto']" data-id="element-119">
                          New Pituitary Gland Extract
                        </p>
                        <p data-id="element-120">
                          <a href="https://peptides.link/endocron" target="_blank" rel="noopener noreferrer nofollow" className="text-[#2290F7] font-bold hover:text-[#1a74d4] transition-colors break-words font-['Roboto']" data-id="element-121">
                            peptides.link/endocron
                          </a>
                        </p>
                      </div>
                      {/* Product 6 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border border-[#E91E63]/30" data-id="element-122">
                        <p className="font-['Roboto']" data-id="element-123">
                          <strong data-id="element-124">Gastrogen</strong>
                        </p>
                        <p className="font-['Roboto'] text-xs text-[#2290F7]" data-id="element-125">
                          v2 Bioregulators
                        </p>
                        <p className="font-['Roboto']" data-id="element-126">Upgraded Stamakort</p>
                        <p data-id="element-127">
                          <a href="https://peptides.link/gastrogen" target="_blank" rel="noopener noreferrer nofollow" className="text-[#2290F7] font-bold hover:text-[#1a74d4] transition-colors break-words font-['Roboto']" data-id="element-128">
                            peptides.link/gastrogen
                          </a>
                        </p>
                      </div>
                      {/* Product 7 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border border-[#2290F7]/30" data-id="element-129">
                        <p className="font-['Roboto']" data-id="element-130">
                          <strong data-id="element-131">Kardium</strong>
                        </p>
                        <p className="font-['Roboto'] text-xs text-[#2290F7]" data-id="element-132">
                          v2 Bioregulators
                        </p>
                        <p className="font-['Roboto']" data-id="element-133">Upgraded Chelohart</p>
                        <p data-id="element-134">
                          <a href="https://peptides.link/kardium" target="_blank" rel="noopener noreferrer nofollow" className="text-[#2290F7] font-bold hover:text-[#1a74d4] transition-colors break-words font-['Roboto']" data-id="element-135">
                            peptides.link/kardium
                          </a>
                        </p>
                      </div>
                      {/* Product 8 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border border-[#E91E63]/30" data-id="element-136">
                        <p className="font-['Roboto']" data-id="element-137">
                          <strong data-id="element-138">Lungard</strong>
                        </p>
                        <p className="font-['Roboto'] text-xs text-[#2290F7]" data-id="element-139">
                          v2 Bioregulators
                        </p>
                        <p className="font-['Roboto']" data-id="element-140">Upgraded Taxorest</p>
                        <p data-id="element-141">
                          <a href="https://peptides.link/lungard" target="_blank" rel="noopener noreferrer nofollow" className="text-[#2290F7] font-bold hover:text-[#1a74d4] transition-colors break-words font-['Roboto']" data-id="element-142">
                            peptides.link/lungard
                          </a>
                        </p>
                      </div>
                      {/* Product 9 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border border-[#2290F7]/30" data-id="element-143">
                        <p className="font-['Roboto']" data-id="element-144">
                          <strong data-id="element-145">Ovalis</strong>
                        </p>
                        <p className="font-['Roboto'] text-xs text-[#2290F7]" data-id="element-146">
                          v2 Bioregulators
                        </p>
                        <p className="font-['Roboto']" data-id="element-147">Upgraded Zhenoluten</p>
                        <p data-id="element-148">
                          <a href="https://peptides.link/ovalis" target="_blank" rel="noopener noreferrer nofollow" className="text-[#2290F7] font-bold hover:text-[#1a74d4] transition-colors break-words font-['Roboto']" data-id="element-149">
                            peptides.link/ovalis
                          </a>
                        </p>
                      </div>
                      {/* Product 10 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border border-[#E91E63]/30" data-id="element-150">
                        <p className="font-['Roboto']" data-id="element-151">
                          <strong data-id="element-152">Prostalon</strong>
                        </p>
                        <p className="font-['Roboto'] text-xs text-[#2290F7]" data-id="element-153">
                          v2 Bioregulators
                        </p>
                        <p className="font-['Roboto']" data-id="element-154">Upgraded Libidon</p>
                        <p data-id="element-155">
                          <a href="https://peptides.link/prostalon" target="_blank" rel="noopener noreferrer nofollow" className="text-[#2290F7] font-bold hover:text-[#1a74d4] transition-colors break-words font-['Roboto']" data-id="element-156">
                            peptides.link/prostalon
                          </a>
                        </p>
                      </div>
                      {/* Product 11 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border border-[#2290F7]/30" data-id="element-157">
                        <p className="font-['Roboto']" data-id="element-158">
                          <strong data-id="element-159">Renolux</strong>
                        </p>
                        <p className="font-['Roboto'] text-xs text-[#2290F7]" data-id="element-160">
                          v2 Bioregulators
                        </p>
                        <p className="font-['Roboto']" data-id="element-161">Upgraded Pielotax</p>
                        <p data-id="element-162">
                          <a href="https://peptides.link/renolux" target="_blank" rel="noopener noreferrer nofollow" className="text-[#2290F7] font-bold hover:text-[#1a74d4] transition-colors break-words font-['Roboto']" data-id="element-163">
                            peptides.link/renolux
                          </a>
                        </p>
                      </div>
                      {/* Product 12 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border border-[#E91E63]/30" data-id="element-164">
                        <p className="font-['Roboto']" data-id="element-165">
                          <strong data-id="element-166">Testovive</strong>
                        </p>
                        <p className="font-['Roboto'] text-xs text-[#2290F7]" data-id="element-167">
                          v2 Bioregulators
                        </p>
                        <p className="font-['Roboto']" data-id="element-168">Upgraded Testoluten</p>
                        <p data-id="element-169">
                          <a href="https://peptides.link/testovive" target="_blank" rel="noopener noreferrer nofollow" className="text-[#2290F7] font-bold hover:text-[#1a74d4] transition-colors break-words font-['Roboto']" data-id="element-170">
                            peptides.link/testovive
                          </a>
                        </p>
                      </div>
                      {/* Product 13 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border border-[#2290F7]/30" data-id="element-171">
                        <p className="font-['Roboto']" data-id="element-172">
                          <strong data-id="element-173">Thymusol</strong>
                        </p>
                        <p className="font-['Roboto'] text-xs text-[#2290F7]" data-id="element-174">
                          v2 Bioregulators
                        </p>
                        <p className="font-['Roboto']" data-id="element-175">
                          Upgraded Vladonix & Thymalin
                        </p>
                        <p data-id="element-176">
                          <a href="https://peptides.link/thymusol" target="_blank" rel="noopener noreferrer nofollow" className="text-[#2290F7] font-bold hover:text-[#1a74d4] transition-colors break-words font-['Roboto']" data-id="element-177">
                            peptides.link/thymusol
                          </a>
                        </p>
                      </div>
                      {/* Product 14 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border border-[#E91E63]/30" data-id="element-178">
                        <p className="font-['Roboto']" data-id="element-179">
                          <strong data-id="element-180">Thyrix</strong>
                        </p>
                        <p className="font-['Roboto'] text-xs text-[#2290F7]" data-id="element-181">
                          v2 Bioregulators
                        </p>
                        <p className="font-['Roboto']" data-id="element-182">Upgraded Thyreogen</p>
                        <p data-id="element-183">
                          <a href="https://peptides.link/thyrix" target="_blank" rel="noopener noreferrer nofollow" className="text-[#2290F7] font-bold hover:text-[#1a74d4] transition-colors break-words font-['Roboto']" data-id="element-184">
                            peptides.link/thyrix
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Third card - Full Peptide Source List */}
        <div id="sources" className="bg-white shadow-sm border border-gray-200 rounded-2xl py-4 my-10 w-full transform transition-all duration-300 hover:shadow-md scroll-mt-20" data-id="element-185">
          <div className="flex w-full" data-id="element-186">
            <div className="w-full px-4" data-id="element-187">
              <div className="flex flex-col justify-center items-normal w-full py-2.5 px-1.5" data-id="element-188">
                {/* Heading */}
                <div className="mt-5" data-id="element-189">
                  <div className="font-['Oswald',sans-serif] text-center" data-id="element-190">
                    <h1 className="text-4xl md:text-[70px] leading-tight md:leading-[91px] font-bold text-[#2290F7]" data-id="element-191">
                      Full Peptide Source List
                    </h1>
                  </div>
                </div>
                {/* Introduction */}
                <div className="mx-4 md:mx-20 my-5" data-id="element-192">
                  <div className="font-['Roboto'] text-center px-2 md:px-[100px] pt-2.5" data-id="element-193">
                    <p className="text-lg md:text-[23px] leading-[34.5px]" data-id="element-194">
                      Over the last decade we've been through many peptide
                      sources. We've hand-picked the ones who can provide you
                      with the best quality and the best purchasing experience.
                      We have partnered with some of these vendors. The links
                      below will take you to the vendors with our highest
                      recommendation.
                    </p>
                    <p className="text-lg md:text-[23px] leading-[34.5px] mt-2" data-id="element-195">
                      <strong data-id="element-196">NOTE: </strong>The peptides on this list are
                      ordered <u data-id="element-197">alphabetically</u>.
                    </p>
                    <p className="text-lg md:text-[23px] leading-[34.5px] mt-4" data-id="element-198"></p>
                  </div>
                </div>
                {/* Peptide Explorer - but with no separate search */}
                <div className="mx-4 md:mx-20 my-5" data-id="element-199">
                  <PeptideExplorer globalSearchQuery={searchQuery} data-id="element-200" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}